import React from 'react';
import { getActivityStats } from '../../activities/activityTypes';
import {
  FIELD_KEYS,
  pick,
  isTruthy,
  DetailTopBar,
  DetailHero,
  WeatherPlaceholder,
} from './ActivityDetailPage';

// Barranquismo-specific field keys (on top of the shared ones).
const BARRANCO_KEYS = {
  style: ['estilo', 'style', 'tipoDescenso'],
  desarrollo: ['desarrollo', 'length', 'longitud', 'dimension', 'recorrido'],
  tiempo: ['tiempo', 'time', 'duracion'],
  neopreno: ['neopreno', 'wetsuit', 'neoprene'],
  agua: ['agua', 'waterTemp', 'temperaturaAgua', 'aguaTemp'],
  cuerda: ['cuerda', 'rope', 'rappelRope'],
};

/**
 * Barranquismo detail page. Follows the v3 "04 · Barranquismo" design: cota
 * belt, hero, beveled title, the three grade cards (vertical / acuática /
 * compromiso — the key datum) and a data row (desarrollo / tiempo / neopreno /
 * agua). Any datum absent from the activity is omitted; weather is a placeholder.
 */
function BarranquismoDetail({ activity }) {
  const values = activity.values || {};
  const description = pick(values, FIELD_KEYS.description);
  const place = pick(values, FIELD_KEYS.location);
  const gpx = pick(values, FIELD_KEYS.gpx);
  const style = pick(values, BARRANCO_KEYS.style);
  const cuerda = pick(values, BARRANCO_KEYS.cuerda);

  // The three grades come from the shared stats spec (Vertical / Acuática /
  // Compromiso). Compromiso is the key datum, highlighted in amber.
  const grades = getActivityStats(activity).filter((s) => s.present);

  const desarrollo = pick(values, BARRANCO_KEYS.desarrollo);
  const tiempo = pick(values, BARRANCO_KEYS.tiempo);
  const neopreno = pick(values, BARRANCO_KEYS.neopreno);
  const agua = pick(values, BARRANCO_KEYS.agua);

  const tiles = [
    desarrollo && { key: 'des', label: 'DESARROLLO', value: desarrollo, unit: 'km' },
    tiempo && { key: 'time', label: 'TIEMPO', value: tiempo, unit: 'h' },
    neopreno && {
      key: 'neo',
      label: 'NEOPRENO',
      value: isTruthy(neopreno) ? 'Obligatorio' : String(neopreno),
    },
    agua && { key: 'agua', label: 'AGUA', value: agua, unit: '°C' },
  ].filter(Boolean);

  return (
    <div className="Detail">
      <DetailTopBar
        activity={activity}
        rightCell={cuerda && <span className="Detail__barCell Detail__barCell--gpx">CUERDA {String(cuerda).toUpperCase()}</span>}
      />

      <div className="Detail__body Detail__barrancoBody">
        {/* Foto a la izquierda (desktop); arriba al apilar en móvil. */}
        <div className="Detail__col Detail__barrancoHeroCol">
          <DetailHero activity={activity} />
        </div>

        <div className="Detail__col">
          <div className="Detail__titleText">
            {[style, place].some(Boolean) && (
              <span className="Detail__eyebrow">
                {[style && String(style).toUpperCase(), place && String(place).toUpperCase()]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            )}
            <h1 className="Detail__title Detail__title--onDark">{activity.name}</h1>
          </div>

          {description && (
            <section className="Detail__section">
              <div className="Detail__sectionLabel">DESCRIPCIÓN</div>
              {String(description)
                .split(/\n{2,}/)
                .map((para, i) => (
                  <p key={i} className="Detail__desc">{para}</p>
                ))}
            </section>
          )}

          {grades.length > 0 && (
            <div className="Detail__grades">
              {grades.map((g) => (
                <div
                  key={g.unit}
                  className={`Detail__gradeCard${g.unit === 'Compromiso' ? ' Detail__gradeCard--accent' : ''}`}
                >
                  <span className="Detail__gradeLabel">{g.unit.toUpperCase()}</span>
                  <span className="Detail__gradeVal">{g.value}</span>
                </div>
              ))}
            </div>
          )}

          {tiles.length > 0 && (
            <div className="Detail__stats Detail__stats--wide">
              {tiles.map(({ key, label, value, unit }) => (
                <div key={key} className="Detail__stat">
                  <span className="Detail__statLabel">{label}</span>
                  <span className="Detail__statVal">
                    {value}
                    {unit && <em className="Detail__statUnit"> {unit}</em>}
                  </span>
                </div>
              ))}
            </div>
          )}

          <WeatherPlaceholder activity={activity} />
        </div>
      </div>

      {gpx && (
        <a className="Detail__gpxFooter" href={gpx} download>
          <span className="Detail__gpxFooterText">
            <span>SIGUIENTE PASO</span>
            <strong>DESCARGAR GPX</strong>
          </span>
          <span className="Detail__gpxFooterIcon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="butt">
              <path d="M12 4 L12 16 M6 11 L12 17 L18 11 M4 20 L20 20" />
            </svg>
          </span>
        </a>
      )}
    </div>
  );
}

export default BarranquismoDetail;
